import type { ReactElement, ReactNode } from 'react'
import { Tooltip as MantineTooltip } from '@mantine/core'

/**
 * The app's tooltip.
 *
 * An empty label renders no tooltip at all. The ranking table relies on it:
 * `UserDisplayName` passes `''` for users who have not made their name public,
 * and Mantine would happily pop up an empty box.
 *
 * 200px wide, 14px/400 centred text, bottom placement, an arrow and a 100ms
 * enter delay.
 *
 * The background is opaque, and a token: translucent black over this app's
 * near-black surfaces left whatever the tooltip covered showing faintly
 * through its own text. It is the token every other floating surface uses --
 * `Select`'s dropdown, `styles.module.css` there -- the page's own background
 * carried up on a hairline and `--shadow-dropdown`.
 *
 * The arrow needs the border spelled out for it. Mantine gives it
 * `background-color: inherit` (so the fill follows on its own) and `border: 0`,
 * then sets the two INNER edges transparent from `getArrowPositionStyles` --
 * so a border here is drawn on exactly the two edges that continue the
 * tooltip's own outline. Without it the arrow is a page-coloured triangle
 * that erases the segment of border it overlaps.
 */
const Tooltip = ({
  label,
  children,
  withinPortal = true,
}: {
  label?: ReactNode
  children: ReactElement
  /** `PremiumLocked` renders its lock inside dialogs and keeps it in place. */
  withinPortal?: boolean
}) => {
  if (label === undefined || label === null || label === '') return children

  return (
    <MantineTooltip
      label={label}
      position="bottom"
      withArrow
      openDelay={100}
      multiline
      withinPortal={withinPortal}
      transitionProps={{ transition: 'fade', duration: 200 }}
      styles={{
        tooltip: {
          maxWidth: '12.5rem',
          whiteSpace: 'normal',
          backgroundColor: 'rgb(var(--primary-background))',
          border: '1px solid rgba(var(--over-primary-background), 0.14)',
          boxShadow: 'var(--shadow-dropdown)',
          color: 'rgb(var(--over-primary-background))',
          fontSize: '0.875rem',
          fontWeight: 400,
          textAlign: 'center',
          borderRadius: '0.5rem',
          padding: '0.375rem 0.625rem',
        },
        arrow: {
          border: '1px solid rgba(var(--over-primary-background), 0.14)',
        },
      }}
    >
      {children}
    </MantineTooltip>
  )
}

export default Tooltip
